import { Button } from "@/shared/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/shared/ui/dialog";
import { AlertTriangle, ExternalLink, Package } from "lucide-react";
import { useTranslation } from "@/shared/i18n";

interface PendingItem {
    id: string;
    name: string;
    published: boolean;
    cover: string;
}

interface ModalDeleteCreatorWarningProps {
    isOpen: boolean;
    onClose: () => void;
    pendingItems: PendingItem[];
}

export function ModalDeleteCreatorWarning({
    isOpen,
    onClose,
    pendingItems = [],
}: ModalDeleteCreatorWarningProps) {
    const { t } = useTranslation('profiles');
    const handleItemClick = (itemId: string) => {
        window.open(`/packs/${itemId}`, '_blank', 'noopener,noreferrer');
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[520px] bg-[#222222] border border-[#333333] rounded-2xl overflow-hidden shadow-xl">

                <DialogHeader className="items-center px-8 pt-8 pb-4 text-center space-y-4">
                    {/* Warning icon */}
                    <div className="p-4 bg-yellow-500/10 rounded-full border border-yellow-500/20">
                        <AlertTriangle className="h-12 w-12 text-yellow-400" />
                    </div>

                    <div className="space-y-2">
                        <DialogTitle className="text-2xl font-semibold text-white">
                            {t('cannotDeactivateProfile')}
                        </DialogTitle>
                        <DialogDescription className="text-gray-300 text-base leading-relaxed max-w-[440px] mx-auto">
                            {t('cannotDeactivateDescription')}
                            <span className="block mt-2 text-sm text-gray-400">
                                {t('clickItemsToResolve')}
                            </span>
                        </DialogDescription>
                    </div>
                </DialogHeader>

                <div className="px-8 py-4">
                    <div className="bg-[#222222] rounded-xl p-6 border border-[#333333]">
                        <div className="flex items-center gap-2 mb-5">
                            <Package className="h-4 w-4 text-gray-400" />
                            <h4 className="text-sm font-medium text-gray-300">
                                {pendingItems.length} {pendingItems.length === 1 ? t('pendingItem') : t('pendingItems')}
                            </h4>
                        </div>

                        <div className="space-y-3 max-h-64 overflow-y-auto">
                            {pendingItems.map((item, index) => (
                                <div
                                    key={item.id}
                                    className="group cursor-pointer"
                                    onClick={() => handleItemClick(item.id)}
                                >
                                    <div className="p-4 rounded-xl bg-[#333333] border border-[#333333] hover:border-[#444444] hover:bg-[#333333] transition-colors">
                                        <div className="flex items-center gap-4">
                                            {/* Number badge */}
                                            <div className="w-6 h-6 rounded-full bg-[#222222] text-blue-300 flex items-center justify-center text-xs font-medium border border-[#333333]">
                                                {index + 1}
                                            </div>

                                            {/* Item image and info */}
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                <img
                                                    src={item.cover}
                                                    alt={item.name}
                                                    className="w-10 h-10 rounded-lg object-cover border border-gray-600"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><rect width="40" height="40" fill="%23374151"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="16">?</text></svg>';
                                                    }}
                                                />

                                                <div className="flex-1 min-w-0">
                                                    <div className="text-white font-medium text-sm group-hover:text-blue-300 transition-colors truncate">
                                                        {item.name}
                                                    </div>
                                                    <div className="text-gray-400 text-xs">
                                                        {item.published ? t('published') : t('draft')}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Action indicator */}
                                            <div className="flex items-center gap-2 text-gray-500 group-hover:text-gray-400">
                                                <span className="text-xs">{t('resolve')}</span>
                                                <ExternalLink className="h-4 w-4" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <DialogFooter className="px-8 pb-8 pt-2">
                    <Button
                        type="button"
                        onClick={onClose}
                        className="w-full h-12 px-8 rounded-xl bg-white hover:bg-gray-100 text-gray-900 text-base font-medium transition-colors"
                    >
                        {t('understood')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}